import { ICorporateUserAutocomplete } from 'corporate/types';
import { VendorMappingState } from 'quotes/enums';
import {
  ICustomerQuote,
  IImportedQuoteData,
  IImportVendorQuoteForm,
  IImportVendorQuotePayload,
  IVendorContactSignal,
  IVendorMappingPrefillVendor,
  IVendorMappingSignal,
  IVendorQuoteNumberSignal,
  IVendorQuoteRevisionSignal,
} from 'quotes/types';

export const VENDOR_QUOTE_REVISION_MAX_LENGTH = 50;

// The two states the BE asks the user to complete the mapping in. Everything else — `mapped`,
// `not_applicable`, and a poll that carries no mapping signal at all (BE not deployed yet) — keeps
// today's behaviour. The state is the ONLY input: never recompute "alternate" to get here.
export const isVendorMappingPromptState = (vendorMapping?: IVendorMappingSignal | null): boolean =>
  VendorMappingState.UNMAPPED === vendorMapping?.state ||
  VendorMappingState.NO_EXTRACTOR === vendorMapping?.state;

// Of the two prompt states only `unmapped` gets a mapping written on finalize — `no_extractor`
// carries no extractor id to key one by (contract §9.2.2), so nothing is saved and the prompt must
// not tell the user their choice will be reused.
export const isVendorMappingRememberedState = (
  vendorMapping?: IVendorMappingSignal | null
): boolean => VendorMappingState.UNMAPPED === vendorMapping?.state;

// Only an explicit `resolved: false` asks: null (re-import) and a poll without the key (BE not
// deployed yet) both keep today's flow, and the BE enforces nothing for them.
export const isVendorQuoteNumberPromptState = (
  vendorQuoteNumber?: IVendorQuoteNumberSignal | null
): boolean => false === vendorQuoteNumber?.resolved;

interface IContactStepFocusOptions {
  hasContact: boolean;
  isVendorLocked: boolean;
  showVendorLocation: boolean;
  showVendorQuoteNumber: boolean;
}

// The first field the user still has to answer on the contact step. The vendor comes first because
// it drives the contact pre-select; a prefilled contact hands the focus on to an owed number.
export const getContactStepFocusElementName = ({
  hasContact,
  isVendorLocked,
  showVendorLocation,
  showVendorQuoteNumber,
}: IContactStepFocusOptions): keyof IImportVendorQuoteForm => {
  if (showVendorLocation && !isVendorLocked) {
    return 'vendorLocation';
  }

  if (showVendorQuoteNumber && hasContact) {
    return 'externalNumber';
  }

  return 'contact';
};

// An alternate importer whose extractor already maps to a vendor: step 1 is never asked for it, but
// shows it read-only whenever it renders (an owed contact or number).
export const isVendorMappingMappedVendorState = (
  vendorMapping?: IVendorMappingSignal | null
): boolean =>
  VendorMappingState.MAPPED === vendorMapping?.state &&
  true === vendorMapping?.vendorRequired &&
  Boolean(vendorMapping?.prefill?.vendor);

// The extraction found the number: step 1 shows it, read-only, beside the detected vendor.
export const isVendorQuoteNumberResolvedState = (
  vendorQuoteNumber?: IVendorQuoteNumberSignal | null
): boolean => true === vendorQuoteNumber?.resolved;

// Label for the vendor-contact autocomplete, shared by the contact-only modal and the wizard step.
// Neither detail is guaranteed — the vendor primary-contact lookup carries no AP number and its
// `company_name` is optional — so the parentheses only appear once there is something to put in
// them, never "Jane Doe ()".
export const getVendorContactLabel = (
  option?: Partial<ICorporateUserAutocomplete> | null,
  isAlternateVersionEnabled = false
): string => {
  const details = [
    option?.companyName,
    isAlternateVersionEnabled && option?.billToAddressAccountsPayableId,
  ]
    .filter(Boolean)
    .join(' - ');

  const name = option?.name ?? '';

  return details ? `${name} (${details})` : name;
};

// The BE enforces each import prompt only for a client that declares it (a number, so multipart
// carries a literal "1", not "true"); other clients calling the same endpoints stay unprompted.
// The number has its own flag: a build that sends only `import_prompt_supported` has no number
// input, so keying the number 422 off it would block those imports outright.
export const getImportPromptPayload = (): Pick<
  IImportVendorQuotePayload,
  'importPromptSupported' | 'vendorQuoteNumberPromptSupported'
> => ({ importPromptSupported: 1, vendorQuoteNumberPromptSupported: 1 });

// Context project: the button default takes precedence over the current vendor quote. A context
// project object with no id does NOT fall back to the quote — it falls through to the extraction's
// project id below (preserves prior behaviour).
const resolveContextProject = (
  quote: ICustomerQuote,
  defaultValues: Partial<IImportVendorQuoteForm>
) => {
  if (defaultValues?.project) {
    return { id: defaultValues.project?.id, name: defaultValues.project?.name };
  }

  if (quote) {
    return { id: quote?.vendorProjectId, name: quote?.vendorProjectName };
  }

  return undefined;
};

// Where the context has no project (global vendor-quote list / costing profile), fall back to the
// project the BE resolved for the extraction (quote_data.project_id) so the headless auto-import
// and the contact-only submit carry a complete project without any FE project lookup. May be
// undefined when neither source has one — project isn't a required field on the contact-only form.
const resolveProject = (
  quote: ICustomerQuote,
  importedQuoteData: IImportedQuoteData,
  defaultValues: Partial<IImportVendorQuoteForm>
) => {
  const contextProject = resolveContextProject(quote, defaultValues);

  if (contextProject?.id) {
    return contextProject;
  }

  if (importedQuoteData?.projectId) {
    return { id: importedQuoteData.projectId };
  }

  return undefined;
};

// The BE saves the extracted value whenever the posted one is blank, so the duplicate check has to
// see that same value too.
export const resolveExtractedQuoteField = (
  typedValue?: string | null,
  extractedValue?: string | null
): string => typedValue?.trim() || extractedValue?.trim() || '';

const mapPrefillVendorToLocation = (vendor?: IVendorMappingPrefillVendor | null) => {
  if (!vendor) {
    return undefined;
  }

  // The picker lists locations by their own name and recovers its search query from the label it
  // built, so the prefill has to read the same way a fresh pick does. The parent vendor's name
  // stays the fallback for a location with no ERP supplier-site link.
  return {
    id: vendor.contactGroupAddressId,
    name: vendor.vendorLocationName || vendor.name,
    accountsPayableId: vendor.accountsPayableId,
  };
};

// The mapping's vendor location, shaped like a picked one so the prompt's Vendor field can render
// it straight away — and ONLY where that field renders, so a form value always means "the user was
// asked". Contract §9.1: outside the two prompt states the vendor bag is not sent at all (on
// `mapped` the prefill is display-only, §8.3), and a non-alternate importer never sends one either;
// in both cases the BE takes the location from the mapping itself.
const resolveVendorLocation = (vendorMapping?: IVendorMappingSignal | null) => {
  const isAsked =
    isVendorMappingPromptState(vendorMapping) && true === vendorMapping?.vendorRequired;

  return mapPrefillVendorToLocation(isAsked ? vendorMapping?.prefill?.vendor : null);
};

// The vendor a `mapped` extractor already holds, kept apart from `vendorLocation` so showing it can
// never put a vendor bag on the finalize.
const resolveMappedVendorLocation = (vendorMapping?: IVendorMappingSignal | null) =>
  mapPrefillVendorToLocation(
    isVendorMappingMappedVendorState(vendorMapping) ? vendorMapping?.prefill?.vendor : null
  );

// A resolved poll signal carries the AlgoDocs-matched vendor contact (new imports); else fall back
// to the mapping's prefill, and finally to the context contact (re-import prefill).
// Only treat the signal as resolved when it carries a real contactId — a resolved signal with a
// null contactId falls through so the payload is never headlessly submitted with a null contact id.
// The mapping prefill only becomes a form value when it carries a `corporateUserId`: that is the id
// `quote[contact_id]` takes, and a null one means the contact can no longer carry a quote.
const resolveContact = (
  defaultValues: Partial<IImportVendorQuoteForm>,
  vendorContact?: IVendorContactSignal | null,
  vendorMapping?: IVendorMappingSignal | null
) => {
  if (vendorContact?.resolved && vendorContact.contactId != null) {
    return {
      id: vendorContact.contactId,
      name: vendorContact.contactName ?? '',
      companyName: vendorContact.vendorCompanyName ?? '',
    };
  }

  const prefilledContact = vendorMapping?.prefill?.vendorContact;

  if (prefilledContact?.corporateUserId != null) {
    return {
      id: prefilledContact.corporateUserId,
      name: prefilledContact.name ?? '',
      companyName: prefilledContact.companyName ?? '',
    };
  }

  return defaultValues?.contact;
};

export const getDefaultFormValues = (
  quote: ICustomerQuote,
  importedQuoteData: IImportedQuoteData,
  defaultValues: Partial<IImportVendorQuoteForm>,
  vendorContact?: IVendorContactSignal | null,
  vendorMapping?: IVendorMappingSignal | null,
  vendorQuoteRevision?: IVendorQuoteRevisionSignal | null
) => {
  const projectData = resolveProject(quote, importedQuoteData, defaultValues);

  // attachTo is always set by the button caller (JOB / PROJECT / COSTING_PROFILE) and drives the
  // import endpoint + association; it is submitted, just not rendered in the contact-only form.
  const attachTo = defaultValues?.attachTo ?? null;

  // importedQuoteData.jobName (BE-computed) only fills where context gave none, so JOB-list hidden
  // jobName + re-import behaviour are identical.
  return {
    project: projectData,
    jobName: defaultValues?.jobName || quote?.jobName || importedQuoteData?.jobName || '',
    contact: resolveContact(defaultValues, vendorContact, vendorMapping),
    vendorLocation: resolveVendorLocation(vendorMapping),
    mappedVendorLocation: resolveMappedVendorLocation(vendorMapping),
    externalProjectName: importedQuoteData?.externalProjectName,
    externalNumber: importedQuoteData?.externalNumber,
    revisionNumber: vendorQuoteRevision
      ? vendorQuoteRevision.value ?? ''
      : importedQuoteData?.revisionNumber,
    attachTo,
  };
};
